// My Jobs (Session AGO) -- ONE unified chronological schedule. Jobs and training sessions appear
// together, grouped by calendar day with sticky date headers, anchored to today: overdue work is
// pinned at the very top, then Today (always shown, even empty), then each later day that has
// work. A compact site search plus type filter pills narrow the whole list client-side; an active
// search flattens the grouping into one date-ordered list. Jobs derive from the offline cache via
// useEngineerData so the list is instant in a no-signal basement; training is live-only (Session
// AGG) and rides the same context (Session AGO), so offline it is whatever was last fetched.
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Search, X } from "lucide-react";
import { useEngineerData } from "./EngineerDataContext";
import { isRemedialJobType } from "../../lib/jobType";
import JobTypeBadge from "../../components/JobTypeBadge";
import { jobReferenceLabel } from "../../lib/jobReference";
import { ENG_CARD, ENG_CARD_LINK, ENG_CARD_OVERDUE_STYLE, ENG_PILL, DANGER_INK, dayWithYear, dayTimeWithYear } from "./engineerUi";
import type { JobView } from "../../lib/offline/types";
import { addDays, isOverdue, isSameLocalDay, localDateKey } from "../../lib/engineerCalendar";
import type { EngineerTrainingSessionDto } from "../../lib/trainingTypes";
import { dayLabelOf, expandWorkDays, jobLinkOf, viewKeyOf } from "../../lib/engineerWorkDays";

// --- Pure helpers (exported for tests) -----------------------------------

// The call-out two-hour rule: logged more than two hours ago and still not picked up (status
// "scheduled"). Once the engineer is attending (in_progress) or it has been submitted
// (pending_approval), it is no longer flagged overdue. The platform has NO "en route" state, so
// this is the honest reading of a stale, unattended call-out.
export const CALLOUT_OVERDUE_MS = 2 * 60 * 60 * 1000;
export function calloutOverdue(job: Pick<JobView, "scheduledFor" | "status">, now: Date): boolean {
  if (job.status !== "scheduled") return false;
  if (!job.scheduledFor) return false;
  const d = new Date(job.scheduledFor);
  if (Number.isNaN(d.getTime())) return false;
  return now.getTime() - d.getTime() > CALLOUT_OVERDUE_MS;
}

// The milliseconds a job is scheduled for, or null when it has no valid date (grouped under
// Unscheduled).
function scheduledMs(job: Pick<JobView, "scheduledFor">): number | null {
  if (!job.scheduledFor) return null;
  const t = new Date(job.scheduledFor).getTime();
  return Number.isNaN(t) ? null : t;
}

// One row of the unified schedule: a job or a training session, normalised to the three facts the
// grouping needs (a sort instant, a calendar-day key, and whether it is overdue).
export type ScheduleItem =
  | { kind: "job"; job: JobView; sortMs: number | null; dayKey: string | null; overdue: boolean }
  | { kind: "training"; session: EngineerTrainingSessionDto; sortMs: number; dayKey: string; overdue: boolean };

// A job's overdue notion is type-specific: the two-hour rule for a call-out, the day-before-today
// rule for everything else (the same pair the pre-AGO sections used).
export function jobItem(job: JobView, now: Date): ScheduleItem {
  const overdue = job.type === "callout" ? calloutOverdue(job, now) : isOverdue(job, now);
  const ms = scheduledMs(job);
  return { kind: "job", job, sortMs: ms, dayKey: ms === null ? null : localDateKey(new Date(ms)), overdue };
}

// A training session's calendar day comes from the ISO string's OWN date part: sessionDate is
// date-only, stored at UTC midnight (server parseSessionDate), so slicing the key can never slip a
// day on a negative-UTC-offset machine the way a local conversion of midnight would. On a UK
// device the two agree. The server only ever sends SCHEDULED sessions, so a session whose day has
// passed is genuinely outstanding and joins the Overdue group rather than stranding a past date
// group above Today.
export function trainingItem(session: EngineerTrainingSessionDto, now: Date): ScheduleItem {
  const dayKey = session.sessionDate.slice(0, 10);
  const t = new Date(session.sessionDate).getTime();
  return { kind: "training", session, sortMs: Number.isNaN(t) ? 0 : t, dayKey, overdue: dayKey < localDateKey(now) };
}

// Every schedule item for the given jobs + training sessions. Completed jobs never appear (they
// live on the calendar and in history).
export function makeScheduleItems(jobs: JobView[], sessions: EngineerTrainingSessionDto[], now: Date): ScheduleItem[] {
  return [
    ...jobs.filter((j) => j.status !== "complete").map((j) => jobItem(j, now)),
    ...sessions.map((s) => trainingItem(s, now)),
  ];
}

export interface ScheduleDayGroup {
  key: string; // local YYYY-MM-DD
  items: ScheduleItem[];
  jobCount: number;
  trainingCount: number;
}

export interface Schedule {
  overdue: ScheduleItem[];
  // Ascending by day. ALWAYS contains today's group, even when it is empty, so the page can say
  // "Nothing scheduled for today" rather than opening on next week's work unannounced.
  days: ScheduleDayGroup[];
  unscheduled: ScheduleItem[];
}

function bySortMs(a: ScheduleItem, b: ScheduleItem): number {
  if (a.sortMs === null && b.sortMs === null) return 0;
  if (a.sortMs === null) return 1;
  if (b.sortMs === null) return -1;
  return a.sortMs - b.sortMs;
}

export function buildSchedule(items: ScheduleItem[], now: Date): Schedule {
  const todayKey = localDateKey(now);
  const overdue: ScheduleItem[] = [];
  const unscheduled: ScheduleItem[] = [];
  const byDay = new Map<string, ScheduleItem[]>();
  for (const item of items) {
    if (item.overdue) {
      overdue.push(item);
    } else if (item.dayKey === null) {
      unscheduled.push(item);
    } else {
      const arr = byDay.get(item.dayKey) ?? [];
      arr.push(item);
      byDay.set(item.dayKey, arr);
    }
  }
  if (!byDay.has(todayKey)) byDay.set(todayKey, []);
  overdue.sort(bySortMs); // oldest first: the most overdue work leads
  const days: ScheduleDayGroup[] = [...byDay.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([key, dayItems]) => {
      dayItems.sort(bySortMs); // training (UTC midnight) naturally leads a day as its all-day item
      return {
        key,
        items: dayItems,
        jobCount: dayItems.filter((i) => i.kind === "job").length,
        trainingCount: dayItems.filter((i) => i.kind === "training").length,
      };
    });
  return { overdue, days, unscheduled };
}

// "Today", "Tomorrow", then "Fri 31 Jul".
export function dayGroupLabel(key: string, now: Date): string {
  if (key === localDateKey(now)) return "Today";
  if (key === localDateKey(addDays(now, 1))) return "Tomorrow";
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

// "2 jobs, 1 training" -- the per-day count badge. Empty when the day has nothing.
export function groupCountLabel(jobCount: number, trainingCount: number): string {
  const parts: string[] = [];
  if (jobCount > 0) parts.push(jobCount === 1 ? "1 job" : `${jobCount} jobs`);
  if (trainingCount > 0) parts.push(trainingCount === 1 ? "1 training" : `${trainingCount} training`);
  return parts.join(", ");
}

export type TypeFilter = "all" | "maintenance" | "repair" | "callout" | "training";

// Maintenance is the catch-all for every non-call-out job type (service / repair / loler and, since
// Session ALW, condition_report / site_inspection), the same reading the pre-AGO Maintenance section
// used. The two new types are deliberately NOT given a filter pill of their own: the engineer's
// filter bar is four pills wide on a phone, an engineer has at most one or two of these in a week,
// and a fifth pill would shrink all of them below the touch target for no real gain. They are still
// told apart on the card, by the type label and the icon.
export function matchesTypeFilter(item: ScheduleItem, filter: TypeFilter): boolean {
  if (filter === "all") return true;
  if (filter === "training") return item.kind === "training";
  if (item.kind !== "job") return false;
  if (filter === "callout") return item.job.type === "callout";
  // Repair is its own pill (Session: job type clarity); Maintenance stays the catch-all for the rest.
  if (filter === "repair") return isRemedialJobType(item.job.type);
  return item.job.type !== "callout" && !isRemedialJobType(item.job.type);
}

// Session ALW: this job has been put on the engineer's schedule and they have NOT accepted it yet.
// `acceptedAt` is optional on the cached job, and an ABSENT value reads as not accepted, which is
// the SAFE direction here (the engineer is prompted once more, rather than a gate silently opening
// on a stale cache). in_progress is excluded for the same reason JobDetail excludes it: the only
// thing that puts a job there is the office returning a submitted sheet, so the work is already
// done and there is nothing left to accept. Pure, exported for its test.
export function awaitingAcceptance(job: Pick<JobView, "status" | "acceptedAt" | "crewMember">): boolean {
  if (job.status !== "scheduled") return false;
  // Session ASM (Phase 6): an engineer booked as additional crew has nothing to accept; the primary does.
  if (job.crewMember) return false;
  return !job.acceptedAt;
}

function itemSiteName(item: ScheduleItem): string {
  return item.kind === "job" ? item.job.siteName : item.session.siteName;
}

// Case-insensitive partial match on the site name. A blank query matches everything.
export function matchesQuery(item: ScheduleItem, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return itemSiteName(item).toLowerCase().includes(q);
}

function itemKey(item: ScheduleItem): string {
  return item.kind === "job" ? `job-${viewKeyOf(item.job)}` : `training-${item.session.id}`;
}

// --- Presentational bits -------------------------------------------------

interface PillSpec {
  label: string;
  bg: string;
  fg: string;
}

// The status pill for a call-out. The platform has NO "en route" state, so every status maps
// honestly: scheduled = waiting to be picked up, in_progress = the engineer is on site,
// pending_approval = written up and with the office.
function calloutPill(status: string): PillSpec {
  if (status === "in_progress") return { label: "Attending", bg: "rgba(99,196,31,0.15)", fg: "var(--ele-green-ink)" };
  if (status === "pending_approval") return { label: "Submitted", bg: "var(--ele-pill-tint-purple)", fg: "var(--ele-pill-ink-purple)" };
  return { label: "Pending", bg: "rgba(184,134,11,0.16)", fg: "var(--ele-amber-text-deep)" }; // scheduled
}

// The status pill for a maintenance visit.
function maintenancePill(status: string): PillSpec {
  if (status === "in_progress") return { label: "In progress", bg: "rgba(44,185,243,0.15)", fg: "var(--ele-loler-ink)" };
  if (status === "pending_approval") return { label: "Closed, with the office", bg: "var(--ele-pill-tint-purple)", fg: "var(--ele-pill-ink-purple)" };
  return { label: "Scheduled", bg: "rgba(156,163,175,0.14)", fg: "var(--ele-text-muted)" }; // scheduled
}

function Pill({ spec }: { spec: PillSpec }) {
  return (
    <span
      className={ENG_PILL}
      style={{ backgroundColor: spec.bg, color: spec.fg }}
    >
      {spec.label}
    </span>
  );
}

function timeOfDay(d: Date): string {
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}
function shortDate(d: Date): string {
  return dayWithYear(d);
}

// The call-out logged time: "Logged 10:15" when it came in today, otherwise a short date. Named
// honestly -- this is when the call-out was logged, not an appointment slot.
function calloutWhenLabel(iso: string | null, now: Date): string {
  if (!iso) return "Time not set";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Time not set";
  return isSameLocalDay(d, now) ? `Logged ${timeOfDay(d)}` : shortDate(d);
}

// A maintenance visit's scheduled date and time with the year, e.g. "Mon 13 Jul 2026, 09:00".
function maintenanceWhenLabel(iso: string | null): string {
  if (!iso) return "Unscheduled";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Unscheduled";
  return dayTimeWithYear(d);
}

// The sync-state line the old home list carried, preserved so the engineer never loses sight of a
// submission that still needs to reach the office. Escalated is amber (it has stopped retrying);
// a still-syncing submission is a quiet muted line with the same wording the home used.
function SyncLine({ job }: { job: JobView }) {
  if (job.syncEscalated) {
    return (
      <div className="mt-2 font-menu text-[11px] uppercase tracking-wide" style={{ color: "var(--ele-amber-text-deep)" }}>
        Needs the office
      </div>
    );
  }
  if (job.pendingSync) {
    return (
      <div className="mt-2 font-menu text-[11px] uppercase tracking-wide" style={{ color: "var(--ele-text-muted)" }}>
        {job.syncError ? "Submitted, will retry sync" : "Submitted, waiting to sync"}
      </div>
    );
  }
  return null;
}

// The shared card shell: a type ICON at the top-left (never a coloured left-border stripe), the
// whole card a 44px-tall link. `to` is a full path so a training card can reuse the exact same
// shell while opening a training session rather than a job sheet (Session AGG).
function CardShell({
  to,
  icon: Icon,
  iconColour,
  iconTint,
  badge,
  overdue = false,
  children,
}: {
  to: string;
  icon?: typeof GraduationCap;
  iconColour?: string;
  iconTint?: string;
  // A job's type badge (icon + label): the first thing read on the card. Training has no job type
  // and keeps its round icon.
  badge?: ReactNode;
  overdue?: boolean;
  children: ReactNode;
}) {
  return (
    <Link to={to} className={ENG_CARD_LINK} style={overdue ? ENG_CARD_OVERDUE_STYLE : undefined}>
      {Icon && (
        <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full" style={{ backgroundColor: iconTint }} aria-hidden="true">
          <Icon size={18} color={iconColour} />
        </span>
      )}
      <div className="min-w-0 flex-1">
        {badge && <div className="mb-1.5">{badge}</div>}
        {children}
      </div>
    </Link>
  );
}

// The canonical job reference (SER-ELE-083-08-10-26), so two visits to one lift are different at a glance.
function EleRef({ job }: { job: JobView }) {
  const label = jobReferenceLabel(job);
  const day = dayLabelOf(job);
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="min-w-0 truncate text-[13px]" title={label} style={{ color: job.eleNumber ? "var(--ele-loler-ink)" : "var(--ele-text-muted)" }}>
        {label}
      </span>
      {/* Session BDM: one day of a multi-day job. The same job, one reference, on each day the engineer is on. */}
      {day && (
        <span className={ENG_PILL} style={{ backgroundColor: "var(--ele-pill-tint-amber)", color: "var(--ele-pill-ink-amber)" }}>
          {day}
        </span>
      )}
    </span>
  );
}

// Session ALW: the one-word prompt that tells an engineer, from the LIST, that a job is waiting on
// them. Green, because it is an invitation to act rather than a warning: the amber "Overdue" and
// "Needs the office" states on these same cards mean something has gone wrong, and this has not.
function AcceptPrompt() {
  return (
    <span className={ENG_PILL} style={{ backgroundColor: "rgba(99,196,31,0.15)", color: "var(--ele-green-ink)" }}>
      Needs accepting
    </span>
  );
}

// Session ASM (Phase 6, D83): this engineer is on the visit as additional crew, not as its lead.
function CrewTag() {
  return (
    <span className={ENG_PILL} style={{ backgroundColor: "rgba(44,185,243,0.15)", color: "var(--ele-loler-ink)" }}>
      Crew
    </span>
  );
}

function CalloutCard({ job, now }: { job: JobView; now: Date }) {
  const overdue = calloutOverdue(job, now);
  return (
    <CardShell to={jobLinkOf(job)} badge={<JobTypeBadge type={job.type} />} overdue={overdue}>
      <div className="flex items-start justify-between gap-2">
        <span className="truncate font-heading text-base font-bold leading-tight text-ele-strong">{job.siteName || "Site to be confirmed"}</span>
        <span className="flex flex-none items-center gap-2">
          {overdue && (
            <span className={ENG_PILL} style={{ backgroundColor: "var(--ele-pill-tint-red)", color: DANGER_INK }}>
              Overdue
            </span>
          )}
          <span className="text-[12px]" style={{ color: "var(--ele-text-muted)" }}>
            {calloutWhenLabel(job.scheduledFor, now)}
          </span>
        </span>
      </div>
      <div className="mt-1 flex">
        <EleRef job={job} />
      </div>
      <div className="mt-1.5 flex items-center justify-end gap-2">
        <span className="flex flex-none items-center gap-1.5">
          {awaitingAcceptance(job) && <AcceptPrompt />}
          {job.crewMember && <CrewTag />}
          <Pill spec={calloutPill(job.status)} />
        </span>
      </div>
      <SyncLine job={job} />
    </CardShell>
  );
}

function MaintenanceCard({ job, now }: { job: JobView; now: Date }) {
  const overdue = isOverdue(job, now);
  return (
    <CardShell to={jobLinkOf(job)} badge={<JobTypeBadge type={job.type} equipmentAsset={job.equipmentAsset === true} />} overdue={overdue}>
      <div className="flex items-start justify-between gap-2">
        <span className="truncate font-heading text-base font-bold leading-tight text-ele-strong">{job.siteName || "Site to be confirmed"}</span>
        <span className="flex-none text-[12px]" style={{ color: overdue ? DANGER_INK : "var(--ele-text-muted)", fontWeight: overdue ? 600 : undefined }}>
          {maintenanceWhenLabel(job.scheduledFor)}
        </span>
      </div>
      <div className="mt-1 flex">
        <EleRef job={job} />
      </div>
      <div className="mt-1.5 flex items-center justify-end gap-2">
        <span className="flex flex-none items-center gap-1.5">
          {awaitingAcceptance(job) && <AcceptPrompt />}
          {job.crewMember && <CrewTag />}
          <Pill spec={maintenancePill(job.status)} />
        </span>
      </div>
      <SyncLine job={job} />
    </CardShell>
  );
}

// A training session card (Session AGG). Same shell as a job card, opening the training screen.
// The type pill is purple, the training accent, so it never reads as a job status.
function TrainingCard({ session }: { session: EngineerTrainingSessionDto }) {
  return (
    <CardShell to={`/engineer/training/${session.id}`} icon={GraduationCap} iconColour="var(--ele-pill-ink-purple)" iconTint="var(--ele-pill-tint-purple)">
      <div className="flex items-start justify-between gap-2">
        <span className="truncate font-heading text-base font-bold leading-tight text-ele-strong">
          {session.siteName || "Site to be confirmed"}
        </span>
        <span className="flex-none text-[12px]" style={{ color: "var(--ele-text-muted)" }}>
          {maintenanceWhenLabel(session.sessionDate)}
        </span>
      </div>
      <div className="mt-1 flex items-center justify-between gap-2">
        <span className="min-w-0 truncate text-[13px]" style={{ color: "var(--ele-text-muted)" }}>
          {session.typeLabel}
        </span>
        <Pill spec={{ label: "Training", bg: "var(--ele-pill-tint-purple)", fg: "var(--ele-pill-ink-purple)" }} />
      </div>
    </CardShell>
  );
}

function ItemCard({ item, now }: { item: ScheduleItem; now: Date }) {
  if (item.kind === "training") return <TrainingCard session={item.session} />;
  return item.job.type === "callout" ? <CalloutCard job={item.job} now={now} /> : <MaintenanceCard job={item.job} now={now} />;
}

// A sticky date header: opaque page ground (it sticks to the viewport top, the shell header having
// scrolled away) and z-10, well under the bottom nav's z-40. The -mx-4/px-4 stretch covers the
// shell's main padding so cards never peek past its edges.
function GroupHeader({ id, label, count, tone }: { id: string; label: string; count: string; tone?: "danger" }) {
  return (
    <div className="sticky top-0 z-10 -mx-4 flex items-baseline gap-2 bg-ele-base px-4 py-2">
      <h2 id={id} className={`font-heading text-lg tracking-wide ${tone === "danger" ? "font-bold" : "text-ele-strong"}`} style={tone === "danger" ? { color: DANGER_INK } : undefined}>
        {label}
      </h2>
      {count && (
        <span
          className="font-menu text-[11px] uppercase tracking-wide"
          style={{ color: tone === "danger" ? DANGER_INK : "var(--ele-text-muted)" }}
        >
          {count}
        </span>
      )}
    </div>
  );
}

const FILTERS: { key: TypeFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "maintenance", label: "Maintenance" },
  { key: "repair", label: "Repair" },
  { key: "callout", label: "Call-out" },
  { key: "training", label: "Training" },
];

export default function MyJobsPage() {
  // Session BDM: dayJobs lays a multi-day job out on every day the engineer is on (one entry per day); a one-day job is itself.
  const { jobs: ownJobs, loading, online, trainingSessions = [], trainingError = false } = useEngineerData();
  const jobs = useMemo(() => expandWorkDays(ownJobs), [ownJobs]);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const now = new Date();

  const items = makeScheduleItems(jobs, trainingSessions, now).filter((i) => matchesTypeFilter(i, typeFilter));
  const searching = query.trim().length > 0;
  const flat = searching ? items.filter((i) => matchesQuery(i, query)).sort((a, b) => {
    if (a.sortMs === null && b.sortMs === null) return 0;
    if (a.sortMs === null) return 1;
    if (b.sortMs === null) return -1;
    return a.sortMs - b.sortMs;
  }) : [];
  const schedule = searching ? null : buildSchedule(items, now);

  // The Session AGG honesty lines, kept: offline (or a failed load) with nothing to show must say
  // so, never read as "no training booked". With sessions on screen the quiet lines stay away.
  const trainingNotice =
    trainingSessions.length > 0 ? null : !online ? "Training sessions require a connection." : trainingError ? "Could not load your training sessions." : null;

  return (
    <div>
      <h1 className="mb-4 font-heading text-2xl tracking-wide text-ele-strong">My jobs</h1>

      {loading && jobs.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--ele-text-muted)" }}>
          Loading your jobs...
        </p>
      ) : (
        <>
          {/* Compact site search. All client-side: no fetch is ever made from here. */}
          <div className="relative">
            <Search
              size={16}
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
              style={{ color: "var(--ele-text-muted)" }}
            />
            <input
              type="text"
              enterKeyHint="search"
              autoComplete="off"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by site..."
              aria-label="Search by site"
              className="min-h-12 w-full rounded-xl border border-ele-card-border bg-ele-card pl-9 pr-11 text-[15px] shadow-[0_1px_3px_var(--ele-shadow)] text-ele-strong placeholder:text-ele-muted focus:outline-none focus:ring-1 focus:ring-[#7d21c7]"
            />
            {query && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setQuery("")}
                className="absolute right-0 top-0 flex min-h-12 min-w-11 items-center justify-center"
                style={{ color: "var(--ele-text-muted)" }}
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Type filter pills. Maintenance covers every non-call-out job type. */}
          <div role="group" aria-label="Filter by type" className="mt-3 flex flex-wrap gap-1.5">
            {FILTERS.map((f) => {
              const active = typeFilter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setTypeFilter(f.key)}
                  className={`min-h-11 flex-1 rounded-full px-1.5 font-menu text-[11px] tracking-wide transition-colors ${
                    active ? "bg-[#7d21c7] text-white" : "border border-ele-card-border bg-ele-card hover:bg-ele-row-hover"
                  }`}
                  style={active ? undefined : { color: "var(--ele-text-muted)" }}
                >
                  {f.label}
                </button>
              );
            })}
          </div>

          {searching ? (
            flat.length === 0 ? (
              <p className="mt-4 text-[13px]" style={{ color: "var(--ele-text-muted)" }}>
                No matches for "{query.trim()}".
              </p>
            ) : (
              <ul className="mt-4 space-y-2" aria-label="Search results">
                {flat.map((item) => (
                  <li key={itemKey(item)}>
                    <ItemCard item={item} now={now} />
                  </li>
                ))}
              </ul>
            )
          ) : (
            <div className="mt-2">
              {schedule && schedule.overdue.length > 0 && (
                <section aria-labelledby="overdue" className="mb-3">
                  <GroupHeader
                    id="overdue"
                    label="Overdue"
                    count={groupCountLabel(
                      schedule.overdue.filter((i) => i.kind === "job").length,
                      schedule.overdue.filter((i) => i.kind === "training").length,
                    )}
                    tone="danger"
                  />
                  <ul className="space-y-2.5">
                    {schedule.overdue.map((item) => (
                      <li key={itemKey(item)}>
                        <ItemCard item={item} now={now} />
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {schedule?.days.map((group) => (
                <section key={group.key} aria-labelledby={`day-${group.key}`} className="mb-3">
                  <GroupHeader id={`day-${group.key}`} label={dayGroupLabel(group.key, now)} count={groupCountLabel(group.jobCount, group.trainingCount)} />
                  {group.items.length === 0 ? (
                    <p className="text-[13px]" style={{ color: "var(--ele-text-muted)" }}>
                      Nothing scheduled for today
                    </p>
                  ) : (
                    <ul className="space-y-2.5">
                      {group.items.map((item) => (
                        <li key={itemKey(item)}>
                          <ItemCard item={item} now={now} />
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}

              {schedule && schedule.unscheduled.length > 0 && (
                <section aria-labelledby="unscheduled" className="mb-3">
                  <GroupHeader id="unscheduled" label="Unscheduled" count={groupCountLabel(schedule.unscheduled.length, 0)} />
                  <ul className="space-y-2.5">
                    {schedule.unscheduled.map((item) => (
                      <li key={itemKey(item)}>
                        <ItemCard item={item} now={now} />
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          )}

          {trainingNotice && (
            <p
              className={`${ENG_CARD} mt-4 p-4 text-center text-[13px]`}
              style={{ color: "var(--ele-text-muted)" }}
            >
              {trainingNotice}
            </p>
          )}
        </>
      )}
    </div>
  );
}
